import { circle, path, svg, type Mountable } from '@llui/dom';

/**
 * Small shared Lucide-derived UI icons (stroke-only, `currentColor`),
 * following the same inline-SVG convention as `entityIcon` (`30-design-system.md
 * §5`): no runtime `lucide` dependency, no icon font, a single first-class DOM
 * node that inherits colour from its container. Decorative by default
 * (`aria-hidden`) — callers pair them with text.
 */

export interface IconOptions {
  /** Pixel size of the square icon (default 16). */
  size?: number;
  /** Extra class on the `<svg>`. */
  className?: string;
}

/** The named glyphs `uiIcon` can render (Lucide names). */
export type UiIconName =
  | 'chevron-down'
  | 'chevron-right'
  | 'plus'
  | 'external-link'
  | 'history'
  | 'pencil'
  | 'x'
  | 'diamond'
  | 'crosshair'
  | 'target'
  | 'sliders-horizontal'
  | 'chevrons-up-down'
  | 'search'
  | 'inbox'
  | 'play-circle'
  | 'map'
  | 'book-open'
  | 'trash-2'
  | 'eye'
  | 'alert-triangle'
  // #266 — flat co-author surfacing: used in the flat Campaign home notice strip.
  | 'git-merge';

/** Shared stroke-only SVG wrapper (matches `entityIcon`: 1.5 stroke, currentColor). */
function strokeIcon(size: number, className: string | undefined, children: Mountable[]): Mountable {
  return svg(
    {
      class: className ? `dl-icon ${className}` : 'dl-icon',
      width: String(size),
      height: String(size),
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    children,
  );
}

/** Lucide path data per icon name (verbatim from lucide-icons/lucide). */
function iconChildren(name: UiIconName): Mountable[] {
  switch (name) {
    case 'chevron-down':
      return [path({ d: 'm6 9 6 6 6-6' })];
    case 'chevron-right':
      return [path({ d: 'm9 18 6-6-6-6' })];
    case 'plus':
      return [path({ d: 'M5 12h14' }), path({ d: 'M12 5v14' })];
    case 'history':
      return [
        path({ d: 'M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' }),
        path({ d: 'M3 3v5h5' }),
        path({ d: 'M12 7v5l4 2' }),
      ];
    case 'pencil':
      return [
        path({
          d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
        }),
        path({ d: 'm15 5 4 4' }),
      ];
    case 'x':
      return [path({ d: 'M18 6 6 18' }), path({ d: 'm6 6 12 12' })];
    case 'diamond':
      return [
        path({
          d: 'M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0Z',
        }),
      ];
    case 'external-link':
      return [
        path({ d: 'M15 3h6v6' }),
        path({ d: 'M10 14 21 3' }),
        path({ d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }),
      ];
    case 'crosshair':
      return [
        circle({ cx: '12', cy: '12', r: '10' }),
        path({ d: 'M22 12h-4' }),
        path({ d: 'M6 12H2' }),
        path({ d: 'M12 6V2' }),
        path({ d: 'M12 22v-4' }),
      ];
    case 'target':
      return [
        circle({ cx: '12', cy: '12', r: '10' }),
        circle({ cx: '12', cy: '12', r: '6' }),
        circle({ cx: '12', cy: '12', r: '2' }),
      ];
    case 'sliders-horizontal':
      return [
        path({ d: 'M10 5H3' }),
        path({ d: 'M12 19H3' }),
        path({ d: 'M14 3v4' }),
        path({ d: 'M16 17v4' }),
        path({ d: 'M21 12h-9' }),
        path({ d: 'M21 19h-5' }),
        path({ d: 'M21 5h-7' }),
        path({ d: 'M8 10v4' }),
        path({ d: 'M8 12H3' }),
      ];
    case 'chevrons-up-down':
      return [path({ d: 'm7 15 5 5 5-5' }), path({ d: 'm7 9 5-5 5 5' })];
    case 'search':
      return [circle({ cx: '11', cy: '11', r: '8' }), path({ d: 'm21 21-4.3-4.3' })];
    case 'inbox':
      return [
        path({ d: 'M22 12h-6l-2 3h-4l-2-3H2' }),
        path({
          d: 'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
        }),
      ];
    case 'play-circle':
      return [circle({ cx: '12', cy: '12', r: '10' }), path({ d: 'm10 8 6 4-6 4z' })];
    case 'map':
      return [
        path({
          d: 'M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z',
        }),
        path({ d: 'M15 5.764v15' }),
        path({ d: 'M9 3.236v15' }),
      ];
    case 'book-open':
      return [
        path({ d: 'M12 7v14' }),
        path({
          d: 'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
        }),
      ];
    case 'trash-2':
      return [
        path({ d: 'M3 6h18' }),
        path({ d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }),
        path({ d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
        path({ d: 'M10 11v6' }),
        path({ d: 'M14 11v6' }),
      ];
    case 'eye':
      return [
        path({
          d: 'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
        }),
        circle({ cx: '12', cy: '12', r: '3' }),
      ];
    case 'alert-triangle':
      return [
        path({
          d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
        }),
        path({ d: 'M12 9v4' }),
        path({ d: 'M12 17h.01' }),
      ];
    // #266 — flat co-author: git-merge glyph (Lucide `git-merge`). Indicates
    // collaborative authoring without hierarchy.
    case 'git-merge':
      return [
        circle({ cx: '18', cy: '18', r: '3' }),
        circle({ cx: '6', cy: '6', r: '3' }),
        path({ d: 'M6 21V9a9 9 0 0 0 9 9' }),
      ];
  }
}

/**
 * Render a named shared UI icon. Used by the topbar (search, auditor chip) and
 * the left rail (campaign switcher chevron, Work-section glyphs).
 */
export function uiIcon(name: UiIconName, opts: IconOptions = {}): Mountable {
  return strokeIcon(opts.size ?? 16, opts.className, iconChildren(name));
}

/**
 * `chevron-down` — the canonical "this opens a panel/menu" affordance. Used by
 * the `dateField` trigger and the native `<select>` (as a CSS data-URI of the
 * same path) so every dropdown caret in the app is one shape.
 */
export function chevronDownIcon(opts: IconOptions = {}): Mountable {
  return uiIcon('chevron-down', opts);
}

/**
 * `chevron-right` — the disclosure caret. Rendered pointing right when the
 * section is closed and rotated 90° when open (CSS owns the rotation), so one
 * glyph serves both states. Replaces the literal `▸` text character, whose
 * weight and baseline never matched the Lucide set beside it.
 */
export function chevronRightIcon(opts: IconOptions = {}): Mountable {
  return uiIcon('chevron-right', opts);
}

/**
 * `plus` — the canonical "create one of these" affordance for icon-only add
 * buttons. Replaces the literal `+` text character, which rendered in the body
 * font at a different weight and optical size than the surrounding icons.
 */
export function plusIcon(opts: IconOptions = {}): Mountable {
  return uiIcon('plus', opts);
}
